import { Gamepad2, Upload } from "lucide-react";
import { consoles } from "../consoles";

/**
 * Built-in bezel previews — self-contained, on-brand content that fills a
 * ShotFrame's screen when no real screenshot has been dropped in. They are NOT
 * mockups of external images: the hero preview reuses the real `consoles` data
 * (genuine product UI), and the showcase preview is a 100% original abstract
 * "CRT powered-on" vibe (no game IP). A real PNG always overrides them.
 */

/** First 10 consoles → a clean 2×5 grid that fills the 16:9 screen. */
const PICKER_CONSOLES = consoles.slice(0, 10);
/** Highlight one tile as "selected", echoing the live picker's focus ring. */
const ACTIVE_INDEX = 1;

/**
 * Hero fallback: a compact, faux Discord-activity "console picker" built from
 * the real console logos — reads as a live product preview, not a placeholder.
 */
export function ConsolePickerPreview() {
  return (
    <div className="lp-preview lp-picker" aria-label="Console picker preview">
      <div className="lp-picker-top">
        <span className="lp-picker-mark" aria-hidden>
          <Gamepad2 size={16} strokeWidth={2} />
        </span>
        <span className="lp-picker-heading">PICK A CONSOLE</span>
        <span className="lp-picker-live">
          <span className="lp-picker-live-dot" aria-hidden />
          in voice
        </span>
      </div>

      <div className="lp-picker-grid" aria-hidden>
        {PICKER_CONSOLES.map((c, i) => (
          <div
            key={c.id}
            className={`lp-picker-tile${i === ACTIVE_INDEX ? " is-active" : ""}`}
          >
            {c.logo ? (
              <img src={c.logo} alt="" loading="lazy" decoding="async" />
            ) : (
              <span style={{ fontSize: "1.4rem", lineHeight: 1 }}>{c.emoji}</span>
            )}
          </div>
        ))}
      </div>

      <div className="lp-picker-foot">
        <Upload size={14} strokeWidth={1.75} aria-hidden />
        drop a <span className="lp-picker-key">.rom</span> to start
      </div>
    </div>
  );
}

/**
 * Showcase fallback: an abstract "CRT powered-on" screen. Generic geometry,
 * scanlines, phosphor glow and original typography only — deliberately evokes
 * no specific, real, copyrighted video game.
 */
export function CrtScreenPreview() {
  return (
    <div className="lp-preview lp-crt" aria-label="Game screen preview">
      <div className="lp-crt-glow" aria-hidden />
      <div className="lp-crt-flicker" aria-hidden />

      <div className="lp-crt-center">
        <span className="lp-crt-start lp-neon lp-crt-blink">&#9654; PRESS START</span>
        <span className="lp-crt-sub">insert rom &middot; player one ready</span>
      </div>

      <div className="lp-crt-hud" aria-hidden>
        <span className="lp-crt-hud-group">
          P1
          <span className="lp-crt-bar" />
        </span>
        <span className="lp-crt-hud-group is-right">
          <span className="lp-crt-pip heart" />
          <span className="lp-crt-pip heart" />
          <span className="lp-crt-pip heart is-spent" />
          <span className="lp-crt-pip coin" />
        </span>
      </div>
    </div>
  );
}
